import React from 'react';

import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import Button from '@asteria/component-core/button';
import Skeleton from '@asteria/component-core/skeleton';
import Table, {
	TableCell,
	TableHeader,
	TableRow,
} from '@asteria/component-core/table';
import { Text, TextGroup } from '@asteria/component-core/typography';

import Chip from '@asteria/component-chip';
import {
	OptionsCell,
	TagsCell,
	TotalCell,
} from '@asteria/component-transactions';
import {
	transformDate,
	transformDescription,
} from '@asteria/component-transactions/utils';

import { Translation } from '@asteria/language';
import { useTranslationFn } from '@asteria/language/hooks';
import { cn } from '@asteria/utils-funcs/classes';

import { Content } from '../../basic';

/**
 * @typedef { import('../../basic/types').Props } Props
 * @typedef { import('../../basic/types').ChildrenProps } ChildrenProps
 */

/**
 * @typedef TransactionTablePagination
 * @property { () => void } next
 * @property { boolean } hasNextPage
 * @property { () => void } back
 * @property { boolean } hasPreviousPage
 * @property { number } page
 *
 * @typedef TransactionTableProps
 * @property { import('@tanstack/react-query').UseQueryResult } query
 * @property { TransactionTablePagination } pagination
 */

function usePaginatedQuery({ onSubmit, startDate, endDate }) {
	const [cursors, setCursors] = React.useState([]);

	const query = useQuery({
		queryKey: [
			'card',
			'transactions',
			{ startDate, endDate },
			'list',
			{ cursor: cursors.at(-1) },
		],
		queryFn: async () => {
			const after = cursors.at(-1);

			const pageFilters = { first: 5, after: after };

			return onSubmit?.('card:fetch', {
				type: 'transactions:details',
				source: {
					startDate: startDate,
					endDate: endDate,
					pageFilters: pageFilters,
				},
			});
		},

		keepPreviousData: true,

		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
	});

	const hasNextPage = query?.data?.pageInfo?.hasNextPage;
	const hasPreviousPage = query?.data?.pageInfo?.hasPreviousPage;

	const endCursor = query?.data?.pageInfo?.endCursor;

	const onNextPageClick = React.useCallback(() => {
		if (hasNextPage) {
			setCursors((prev) => prev.concat(endCursor));
		}
	}, [endCursor, hasNextPage]);

	const onPreviousPageClick = React.useCallback(() => {
		if (hasPreviousPage) {
			setCursors((prev) => prev.slice(0, -1));
		}
	}, [hasPreviousPage]);

	return [
		query,
		React.useMemo(
			() => ({
				page: cursors.length + 1,
				next: onNextPageClick,
				hasNextPage: hasNextPage,
				back: onPreviousPageClick,
				hasPreviousPage: hasPreviousPage,
			}),
			[
				cursors.length,
				hasNextPage,
				hasPreviousPage,
				onNextPageClick,
				onPreviousPageClick,
			],
		),
	];
}

/** @type { React.FC<ChildrenProps & TransactionTableProps> } */
const TransactionTable = React.memo(function TransactionTable(props) {
	const { onAction, onSubmit, translationOptions, query, pagination } = props;

	const translate = useTranslationFn();

	const loading = query.isFetching;

	return (
		<div className="flex flex-col gap-2">
			<div className="overflow-x-auto">
				<div
					className={cn('min-w-max', {
						'min-w-0': !query?.data?.edges?.length,
					})}
				>
					<Table className="relative">
						{!loading && !query?.data?.edges?.length ? (
							<div className="absolute inset-0 flex items-center justify-center backdrop-blur-sm">
								<Translation
									translationKey="card.content.table.empty"
									translationOptions={translationOptions}
									Component={Text}
									size="sm"
								/>
							</div>
						) : null}
						<TableHeader>
							<TableCell className="asteria-component__card-table-cell">
								<Translation
									translationKey="card.content.table.header"
									translationOptions={{
										...translationOptions,
										postfix: {
											...translationOptions?.postfix,
											cell: 'description',
										},
									}}
									Component={Text}
									size="sm"
								/>
							</TableCell>
							<TableCell className="asteria-component__card-table-cell">
								<Translation
									translationKey="card.content.table.header"
									translationOptions={{
										...translationOptions,
										postfix: {
											...translationOptions?.postfix,
											cell: 'date',
										},
									}}
									Component={Text}
									size="sm"
								/>
							</TableCell>
							<TableCell className="asteria-component__card-table-cell">
								<Translation
									translationKey="card.content.table.header"
									translationOptions={{
										...translationOptions,
										postfix: {
											...translationOptions?.postfix,
											cell: 'category',
										},
									}}
									Component={Text}
									size="sm"
								/>
							</TableCell>
							<TableCell className="asteria-component__card-table-cell">
								<Translation
									translationKey="card.content.table.header"
									translationOptions={{
										...translationOptions,
										postfix: {
											...translationOptions?.postfix,
											cell: 'status',
										},
									}}
									Component={Text}
									size="sm"
								/>
							</TableCell>
							<TableCell
								className={cn(
									'text-right justify-end',
									'asteria-component__card-table-cell',
								)}
							>
								<Translation
									translationKey="card.content.table.header"
									translationOptions={{
										...translationOptions,
										postfix: {
											...translationOptions?.postfix,
											cell: 'amount',
										},
									}}
									Component={Text}
									size="sm"
								/>
							</TableCell>
							<TableCell className="asteria-component__card-table-cell">
								<Translation
									translationKey="card.content.table.header"
									translationOptions={{
										...translationOptions,
										postfix: {
											...translationOptions?.postfix,
											cell: 'actions',
										},
									}}
									Component={Text}
									size="sm"
								/>
							</TableCell>
						</TableHeader>
						{loading || !query?.data?.edges?.length
							? Array.from({ length: 5 }).map((_, index) => (
									<TableRow key={index}>
										<TableCell className="asteria-component__card-table-cell">
											<Skeleton animated={loading} />
										</TableCell>
										<TableCell className="asteria-component__card-table-cell">
											<Skeleton animated={loading} />
										</TableCell>
										<TableCell className="asteria-component__card-table-cell">
											<Skeleton animated={loading} />
										</TableCell>
										<TableCell className="asteria-component__card-table-cell">
											<Skeleton animated={loading} />
										</TableCell>
										<TableCell
											className={cn(
												'text-right justify-end',
												'asteria-component__card-table-cell',
											)}
										>
											<Skeleton animated={loading} />
										</TableCell>
										<TableCell className="asteria-component__card-table-cell">
											<Skeleton animated={loading} />
										</TableCell>
									</TableRow>
							  ))
							: (query?.data?.edges ?? []).map(({ node }) => {
									const status =
										node?.status?.toLowerCase?.() || 'paid';

									return (
										<TableRow key={node?._id ?? node?.id}>
											<TableCell className="asteria-component__card-table-cell">
												<Text
													title={transformDescription(
														node?.description,
													)}
													size="sm"
												>
													{transformDescription(
														node?.description,
													)}
												</Text>
											</TableCell>
											<TableCell className="asteria-component__card-table-cell">
												<Text size="sm">
													{transformDate(
														node?.paymentDate,
													)}
												</Text>
											</TableCell>
											<TableCell className="asteria-component__card-table-cell">
												<TagsCell
													className="flex items-center gap-2"
													onAction={onAction}
													onSubmit={onSubmit}
													data={node}
													chip={{
														variant: 'simple',
														size: 'md',
													}}
												/>
											</TableCell>
											<TableCell className="asteria-component__card-table-cell">
												<Chip
													colors={
														[
															'unpaid',
															'signed',
															'overdue',
														].includes(status)
															? status
															: null
													}
													label={translate(
														[
															`list.transactions.transaction.status`,
															`list.transactions.transaction.status.${status}`,
														],
														{ default: status },
													)}
													variant="simple"
												/>
											</TableCell>
											<TableCell
												className={cn(
													'text-right justify-end',
													'asteria-component__card-table-cell',
												)}
											>
												<TotalCell
													className="flex items-center gap-2"
													onAction={onAction}
													onSubmit={onSubmit}
													data={node}
													size="sm"
												/>
											</TableCell>
											<TableCell className="asteria-component__card-table-cell">
												<OptionsCell
													onAction={onAction}
													onSubmit={onSubmit}
													data={node}
												/>
											</TableCell>
										</TableRow>
									);
							  })}
					</Table>
				</div>
			</div>
			{pagination.hasPreviousPage || pagination.hasNextPage ? (
				<div className="flex items-center justify-center gap-2">
					<Button
						icon="chevron-left"
						size="sm"
						onClick={pagination.back}
						disabled={!pagination.hasPreviousPage || loading}
					/>
					<Button
						icon="chevron-right"
						size="sm"
						onClick={pagination.next}
						disabled={!pagination.hasNextPage || loading}
					/>
				</div>
			) : null}
		</div>
	);
});

TransactionTable.propTypes = {
	onAction: PropTypes.func,
	onSubmit: PropTypes.func,
	startDate: PropTypes.string,
	endDate: PropTypes.string,
	loading: PropTypes.bool,
	importing: PropTypes.bool,
	translationOptions: PropTypes.object,
	query: PropTypes.object,
	pagination: PropTypes.object,
};

/** @type { React.FC<ChildrenProps> } */
const CardContent = React.memo(function CardContent(props) {
	const { loading, importing, translationOptions } = props;

	const animated = loading || importing;

	const [query, pagination] = usePaginatedQuery(props);

	const nextTranslationOptions = React.useMemo(
		() => ({
			...translationOptions,
			data: {
				...translationOptions?.data,
				source: {
					...translationOptions?.data?.source,
					available: query?.data?.edges?.length,
					shown: query?.data?.edges?.length * pagination.page,
				},
			},
		}),
		[pagination.page, query?.data?.edges?.length, translationOptions],
	);

	return (
		<div className="flex flex-col gap-4">
			<TextGroup>
				{animated ? (
					<Skeleton animated count={2} />
				) : (
					<>
						<Content.Title
							{...props}
							showLoading={animated}
							loading={animated}
							translationOptions={nextTranslationOptions}
						/>
						<Content.Text
							{...props}
							showLoading={animated}
							loading={animated}
							translationOptions={nextTranslationOptions}
						/>
					</>
				)}
			</TextGroup>
			<TransactionTable
				{...props}
				query={query}
				pagination={pagination}
			/>
		</div>
	);
});

CardContent.propTypes = {
	translationOptions: PropTypes.object,
	loading: PropTypes.bool,
	importing: PropTypes.bool,
};

export default CardContent;
